import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Truck, Clock, Package, CheckCircle, AlertTriangle, FileText, Printer, Camera, X, Plus, TrendingUp } from 'lucide-react';

import { API } from '@/config';

export default function TruckDetailPage() {
  const { truck_id } = useParams();
  const navigate = useNavigate();
  const [truck, setTruck] = useState(null);
  const [poLineItems, setPoLineItems] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState({});
  const [activity, setActivity] = useState([]);
  const [palletPicks, setPalletPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPalletModal, setShowAddPalletModal] = useState(false);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [inspectionData, setInspectionData] = useState({
    passed: null,
    notes: ''
  });
  const [newPalletPick, setNewPalletPick] = useState({
    lp_number: '',
    item_id: '',
    quantity: 0,
    operator: '',
    photos: []
  });

  useEffect(() => {
    fetchTruckDetails();
    // Refresh every 10 seconds for real-time updates
    const interval = setInterval(fetchTruckDetails, 10000);
    return () => clearInterval(interval);
  }, [truck_id]);

  const fetchTruckDetails = async () => {
    try {
      // Fetch truck details
      const trucksResponse = await axios.get(`${API}/warehouse/trucks`);
      const truckData = trucksResponse.data.find(t => t.truck_id === truck_id);
      setTruck(truckData);

      // Fetch PO line items if this is a delivery
      if (truckData?.po_number && truckData.purpose === 'delivery') {
        // For demo, create mock PO line items
        // In production, would fetch from GET /api/purchase-orders/{po_number}
        const mockPoItems = [
          { item_id: '1', item_name: 'Ground Beef 80/20', expected_quantity: 1000, uom: 'lbs' },
          { item_id: '2', item_name: 'Flour Tortillas 10"', expected_quantity: 5000, uom: 'each' },
          { item_id: '3', item_name: 'Cheddar Cheese Shred', expected_quantity: 500, uom: 'lbs' }
        ];
        setPoLineItems(mockPoItems);
      }

      // Fetch pallet picks and calculate loading progress
      const picks = truckData?.pallet_picks || [];
      setPalletPicks(picks);
      
      // Calculate loaded quantities per item
      const progress = {};
      picks.forEach(pick => {
        if (pick.item_id) {
          progress[pick.item_id] = (progress[pick.item_id] || 0) + pick.quantity;
        }
      });
      setLoadingProgress(progress);

      // Build activity timeline
      const activityLog = [];
      if (truckData?.actual_arrival_time) {
        activityLog.push({
          time: truckData.actual_arrival_time,
          action: 'Truck checked in',
          user: truckData.driver_name || 'Driver',
          status: 'completed'
        });
      }
      if (truckData?.inspection_passed_at) {
        activityLog.push({
          time: truckData.inspection_passed_at,
          action: 'Trailer inspection passed',
          user: truckData.inspection_inspector || 'QA Inspector',
          status: 'completed'
        });
      }
      if (truckData?.loading_started_at) {
        activityLog.push({
          time: truckData.loading_started_at,
          action: 'Loading started',
          user: 'Warehouse Operator',
          status: 'completed'
        });
      }
      setActivity(activityLog);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching truck details:', error);
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    try {
      let updates = {};
      const isDelivery = truck.purpose === 'delivery';
      
      switch (action) {
        case 'check-in':
          updates = { 
            status: 'checked_in',
            actual_arrival_time: new Date().toISOString()
          };
          await axios.put(`${API}/warehouse/trucks/${truck_id}`, updates);
          alert('Truck checked in successfully!');
          break;
          
        case 'trailer-inspection':
          // Open inspection modal instead of simple confirm
          setShowInspectionModal(true);
          return; // Don't refresh yet, wait for modal submission
          
        case 'start-loading':
          updates = { 
            status: 'loading',
            loading_started_at: new Date().toISOString()
          };
          await axios.put(`${API}/warehouse/trucks/${truck_id}`, updates);
          alert('Loading started!');
          break;
          
        case 'start-unloading':
          updates = { status: 'unloading' };
          await axios.put(`${API}/warehouse/trucks/${truck_id}`, updates);
          alert('Unloading started!');
          break;
          
        case 'complete-loading':
          updates = { status: 'loading_complete' };
          await axios.put(`${API}/warehouse/trucks/${truck_id}`, updates);
          alert('Loading completed!');
          break;
          
        case 'unloading-complete':
          updates = { status: 'unloading_complete' };
          await axios.put(`${API}/warehouse/trucks/${truck_id}`, updates);
          alert('Unloading completed!');
          break;
          
        case 'generate-bol':
          // Generate BOL and mark driver released
          updates = { status: 'driver_released' };
          await axios.put(`${API}/warehouse/trucks/${truck_id}`, updates);
          alert('BOL generated and driver released!');
          // In production, would trigger PDF generation
          break;
          
        case 'complete-receipt':
          updates = { status: 'driver_released' };
          await axios.put(`${API}/warehouse/trucks/${truck_id}`, updates);
          alert('Receipt completed and driver released!');
          break;
          
        case 'mark-departed':
        case 'release-driver':
          updates = { 
            status: 'departed',
            actual_departure_time: new Date().toISOString()
          };
          await axios.put(`${API}/warehouse/trucks/${truck_id}`, updates);
          alert('Truck departed!');
          break;
          
        default:
          alert(`${action} functionality coming soon!`);
      }
      
      // Refresh truck details
      await fetchTruckDetails();
    } catch (error) {
      console.error(`Error performing ${action}:`, error);
      alert(`Failed to perform ${action}. Please try again.`);
    }
  };

  const handleInspectionSubmit = async () => {
    try {
      const isDelivery = truck.purpose === 'delivery';
      
      if (inspectionData.passed) {
        const updates = {
          status: isDelivery ? 'ready_to_load' : 'ready_to_unload',
          inspection_passed_at: new Date().toISOString(),
          inspection_inspector: 'Current User', // TODO: Get from auth context
          inspection_notes: inspectionData.notes
        };
        await axios.put(`${API}/warehouse/trucks/${truck_id}`, updates);
        alert('Inspection passed! Trailer is ready.');
      } else {
        // Failed inspection
        const updates = {
          inspection_failed_at: new Date().toISOString(),
          inspection_inspector: 'Current User', // TODO: Get from auth context
          inspection_notes: inspectionData.notes
        };
        await axios.put(`${API}/warehouse/trucks/${truck_id}`, updates);
        alert('Inspection failed. Trailer flagged for issues.');
      }
      
      setShowInspectionModal(false);
      setInspectionData({ passed: null, notes: '' });
      await fetchTruckDetails();
    } catch (error) {
      console.error('Error submitting inspection:', error);
      alert('Failed to submit inspection');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPhoto(true);
    
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setNewPalletPick({
          ...newPalletPick,
          photos: [...newPalletPick.photos, base64String]
        });
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading photo:', error);
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = (index) => {
    setNewPalletPick({
      ...newPalletPick,
      photos: newPalletPick.photos.filter((_, i) => i !== index)
    });
  };

  const handleAddPalletPick = async () => {
    if (!newPalletPick.lp_number || !newPalletPick.operator || !newPalletPick.item_id || !newPalletPick.quantity) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const pick = {
        ...newPalletPick,
        timestamp: new Date().toISOString(),
        pick_id: `PICK-${Date.now()}`
      };

      const updatedPicks = [...palletPicks, pick];
      
      // Update truck with new pallet pick
      await axios.put(`${API}/warehouse/trucks/${truck_id}`, {
        pallet_picks: updatedPicks
      });

      setPalletPicks(updatedPicks);
      
      // Update loading progress
      const newProgress = { ...loadingProgress };
      newProgress[pick.item_id] = (newProgress[pick.item_id] || 0) + pick.quantity;
      setLoadingProgress(newProgress);
      
      setShowAddPalletModal(false);
      setNewPalletPick({ lp_number: '', item_id: '', quantity: 0, operator: '', photos: [] });
      alert('Pallet pick logged successfully!');
      await fetchTruckDetails();
    } catch (error) {
      console.error('Error logging pallet pick:', error);
      alert('Failed to log pallet pick');
    }
  };

  if (loading || !truck) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Loading truck details...</div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-blue-100 text-blue-700',
      checked_in: 'bg-green-100 text-green-700',
      truck_arrived: 'bg-green-100 text-green-700',
      ready_to_load: 'bg-cyan-100 text-cyan-700',
      ready_to_unload: 'bg-cyan-100 text-cyan-700',
      loading: 'bg-yellow-100 text-yellow-700',
      unloading: 'bg-yellow-100 text-yellow-700',
      loading_complete: 'bg-purple-100 text-purple-700',
      unloading_complete: 'bg-purple-100 text-purple-700',
      driver_released: 'bg-indigo-100 text-indigo-700',
      departed: 'bg-gray-100 text-gray-700'
    };
    return (
      <span className={`px-3 py-1 rounded text-sm font-semibold ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {status.replace(/_/g, ' ').toUpperCase()}
      </span>
    );
  };

  const isDelivery = truck.purpose === 'delivery';
  
  const getTemperatureDisplay = (tempType) => {
    const temps = {
      dry: { label: 'Dry / Ambient', temp: 'Ambient', icon: '🌡️' },
      refrigerated: { label: 'Refrigerated', temp: '34°F', icon: '❄️' },
      frozen: { label: 'Frozen', temp: '-10°F', icon: '🧊' }
    };
    return temps[tempType] || temps.dry;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header with Status and Actions */}
      <div className="flex items-start justify-between gap-6">
        {/* Left side - Title */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/warehouse/trucks')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Truck className="w-8 h-8" />
              {truck.confirmation_number}
            </h1>
            <p className="text-gray-600 mt-1">{truck.carrier} - {truck.purpose}</p>
          </div>
        </div>

        {/* Right side - Appointment Details with Actions */}
        <div className="flex gap-4">
          {/* Appointment Details Card */}
          <div className="bg-white rounded-lg shadow p-4" style={{ minWidth: '450px' }}>
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-bold text-lg">Appointment Details</h3>
              {getStatusBadge(truck.status)}
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <span>{new Date(truck.appointment_date).toLocaleDateString()} {truck.appointment_time}</span>
              </div>
              <div>
                <span className="text-gray-500">{truck.purpose === 'delivery' ? 'PO #' : 'SO #'}:</span>{' '}
                <span className="font-semibold">{truck.linked_po_number || truck.linked_so_number || '-'}</span>
              </div>
              <div>
                <span className="text-gray-500">Dock:</span>{' '}
                <span className="font-semibold">{truck.dock_door_id || 'Not assigned'}</span>
              </div>
              <div>
                <span className="text-gray-500">Temperature:</span>{' '}
                <span className="font-semibold">
                  {getTemperatureDisplay(truck.temperature_type || 'dry').icon} {getTemperatureDisplay(truck.temperature_type || 'dry').temp}
                </span>
              </div>
              {truck.driver_name && (
                <>
                  <div>
                    <span className="text-gray-500">Driver:</span> <span className="font-semibold">{truck.driver_name}</span>
                  </div>
                  {truck.license_plate && (
                    <div>
                      <span className="text-gray-500">Plate:</span> <span className="font-semibold font-mono">{truck.license_plate}</span>
                    </div>
                  )}
                </>
              )}
            </div>
            {truck.notes && (
              <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                <div className="font-semibold text-yellow-900">Notes:</div>
                <div className="text-yellow-800">{truck.notes}</div>
              </div>
            )}
          </div>

          {/* Action Buttons Card */}
          <div className="bg-white rounded-lg shadow p-4" style={{ minWidth: '250px' }}>
            <h3 className="font-bold text-lg mb-3">Actions</h3>
            <div className="flex flex-col gap-2">
          {/* Check In (both workflows) */}
          {truck.status === 'scheduled' && (
            <button
              onClick={() => handleAction('check-in')}
              className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm w-full justify-start"
            >
              <CheckCircle className="w-4 h-4" />
              Check In Truck
            </button>
          )}
          
          {/* Trailer Inspection (both workflows) */}
          {(truck.status === 'checked_in' || truck.status === 'truck_arrived') && (
            <button
              onClick={() => handleAction('trailer-inspection')}
              className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm w-full justify-start"
            >
              <FileText className="w-4 h-4" />
              Trailer Inspection
            </button>
          )}

          {/* DELIVERY WORKFLOW (Receipts - incoming goods) */}
          {isDelivery && (
            <>
              {truck.status === 'ready_to_load' && (
                <button
                  onClick={() => handleAction('start-loading')}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm w-full justify-start"
                >
                  <Package className="w-4 h-4" />
                  Start Loading
                </button>
              )}

              {truck.status === 'loading' && (
                <>
                  <button
                    onClick={() => setShowAddPalletModal(true)}
                    className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm w-full justify-start"
                  >
                    <Plus className="w-4 h-4" />
                    Log Pallet Pick
                  </button>
                  <button
                    onClick={() => handleAction('complete-loading')}
                    className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm w-full justify-start"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Complete Loading
                  </button>
                </>
              )}

              {truck.status === 'loading_complete' && (
                <button
                  onClick={() => handleAction('generate-bol')}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm w-full justify-start"
                >
                  <FileText className="w-4 h-4" />
                  Generate BOL & Release Driver
                </button>
              )}

              {truck.status === 'driver_released' && (
                <button
                  onClick={() => handleAction('mark-departed')}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm w-full justify-start"
                >
                  <Truck className="w-4 h-4" />
                  Mark Departed
                </button>
              )}
            </>
          )}

          {/* SHIPMENT WORKFLOW (outgoing goods) */}
          {!isDelivery && (
            <>
              {truck.status === 'ready_to_unload' && (
                <button
                  onClick={() => handleAction('start-unloading')}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm w-full justify-start"
                >
                  <Package className="w-4 h-4" />
                  Start Unloading
                </button>
              )}

              {truck.status === 'unloading' && (
                <>
                  <button
                    onClick={() => setShowAddPalletModal(true)}
                    className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm w-full justify-start"
                  >
                    <Plus className="w-4 h-4" />
                    Log Pallet Received
                  </button>
                  <button
                    onClick={() => handleAction('unloading-complete')}
                    className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm w-full justify-start"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Unloading Complete
                  </button>
                </>
              )}

              {truck.status === 'unloading_complete' && (
                <button
                  onClick={() => handleAction('complete-receipt')}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm w-full justify-start"
                >
                  <FileText className="w-4 h-4" />
                  Complete Receipt & Release Driver
                </button>
              )}

              {truck.status === 'driver_released' && (
                <button
                  onClick={() => handleAction('release-driver')}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm w-full justify-start"
                >
                  <Truck className="w-4 h-4" />
                  Release Driver (Departed)
                </button>
              )}
            </>
          )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Real-time Activity */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">Real-Time Activity</h2>
          <div className="space-y-3">
            {activity.length > 0 ? (
              activity.map((item, index) => (
                <div key={index} className="flex items-start gap-3 p-3 border rounded">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    item.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500'
                  }`}></div>
                  <div className="flex-1">
                    <div className="font-semibold">{item.action}</div>
                    <div className="text-sm text-gray-600">{item.user}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {new Date(item.time).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-8">No activity yet</div>
            )}
          </div>
        </div>

        {/* PO Line Items - Loading Progress */}
        {isDelivery && poLineItems.length > 0 && truck.status === 'loading' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Package className="w-6 h-6" />
              Purchase Order Items - Loading Progress
            </h2>
            <div className="space-y-4">
              {poLineItems.map((item) => {
                const loadedQty = loadingProgress[item.item_id] || 0;
                const percentage = item.expected_quantity > 0 
                  ? Math.min(100, Math.round((loadedQty / item.expected_quantity) * 100))
                  : 0;
                
                return (
                  <div key={item.item_id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="font-semibold text-lg">{item.item_name}</div>
                        <div className="text-sm text-gray-600 mt-1">
                          {loadedQty} / {item.expected_quantity} {item.uom} loaded
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-2xl font-bold ${
                          percentage >= 100 ? 'text-green-600' :
                          percentage >= 50 ? 'text-blue-600' :
                          'text-gray-600'
                        }`}>
                          {percentage}%
                        </div>
                        {percentage >= 100 && (
                          <div className="text-xs text-green-600 flex items-center gap-1 mt-1">
                            <CheckCircle className="w-3 h-3" />
                            Complete
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${
                          percentage >= 100 ? 'bg-green-500' :
                          percentage >= 50 ? 'bg-blue-500' :
                          'bg-gray-400'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              
              {/* Overall Progress */}
              <div className="border-t-2 pt-4 mt-4">
                <div className="flex items-center justify-between">
                  <div className="font-bold text-lg">Overall Progress</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {Math.round(
                      poLineItems.reduce((acc, item) => {
                        const loadedQty = loadingProgress[item.item_id] || 0;
                        const percentage = item.expected_quantity > 0 
                          ? Math.min(100, (loadedQty / item.expected_quantity) * 100)
                          : 0;
                        return acc + percentage;
                      }, 0) / poLineItems.length
                    )}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pallet Picks - Detailed Tracking */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">
            Pallet {isDelivery ? 'Picks' : 'Receipts'} ({palletPicks.length})
          </h2>
          <div className="space-y-4">
            {palletPicks.length > 0 ? (
              palletPicks.map((pick, index) => (
                <div key={pick.pick_id || index} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="font-mono text-lg font-semibold text-blue-600">
                        {pick.lp_number}
                      </div>
                      {pick.item_id && (
                        <div className="font-medium text-gray-900 mt-1">
                          {poLineItems.find(i => i.item_id === pick.item_id)?.item_name || 'Item'}: {pick.quantity} {poLineItems.find(i => i.item_id === pick.item_id)?.uom || 'units'}
                        </div>
                      )}
                      <div className="text-sm text-gray-600 mt-1 flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {new Date(pick.timestamp).toLocaleString()}
                        </span>
                        <span className="font-medium">Operator: {pick.operator}</span>
                      </div>
                    </div>
                  </div>
                  
                  {pick.photos && pick.photos.length > 0 && (
                    <div className="mt-3">
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        Photos ({pick.photos.length})
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        {pick.photos.map((photo, photoIndex) => (
                          <img
                            key={photoIndex}
                            src={photo}
                            alt={`Pallet ${pick.lp_number} - Photo ${photoIndex + 1}`}
                            className="w-full h-24 object-cover rounded border hover:scale-105 transition-transform cursor-pointer"
                            onClick={() => window.open(photo, '_blank')}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-8">
                <Package className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                <p>No pallets {isDelivery ? 'picked' : 'received'} yet</p>
                <p className="text-sm mt-1">Use "Log Pallet {isDelivery ? 'Pick' : 'Received'}" button to track pallets</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Pallet Pick Modal */}
      {showAddPalletModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="border-b p-4">
              <h2 className="text-xl font-bold">Log Pallet {isDelivery ? 'Pick' : 'Receipt'}</h2>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  License Plate Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newPalletPick.lp_number}
                  onChange={(e) => setNewPalletPick({ ...newPalletPick, lp_number: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="LP-YYYYMMDD-XXXX"
                  required
                />
              </div>
              
              {/* Item Selection */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Item <span className="text-red-500">*</span>
                </label>
                <select
                  value={newPalletPick.item_id}
                  onChange={(e) => setNewPalletPick({ ...newPalletPick, item_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select item...</option>
                  {poLineItems.map((item) => (
                    <option key={item.item_id} value={item.item_id}>
                      {item.item_name} (Expected: {item.expected_quantity} {item.uom})
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Quantity <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newPalletPick.quantity}
                  onChange={(e) => setNewPalletPick({ ...newPalletPick, quantity: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter quantity"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">
                  Operator Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newPalletPick.operator}
                  onChange={(e) => setNewPalletPick({ ...newPalletPick, operator: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter operator name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">
                  Photos
                </label>
                {newPalletPick.photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    {newPalletPick.photos.map((photo, index) => (
                      <div key={index} className="relative">
                        <img
                          src={photo}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-24 object-cover rounded border"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemovePhoto(index)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                  <Camera className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {uploadingPhoto ? 'Uploading...' : 'Add Photo'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={uploadingPhoto}
                  />
                </label>
              </div>
            </div>
            
            <div className="border-t p-4 flex gap-3">
              <button
                onClick={() => {
                  setShowAddPalletModal(false);
                  setNewPalletPick({ lp_number: '', item_id: '', quantity: 0, operator: '', photos: [] });
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddPalletPick}
                disabled={!newPalletPick.lp_number || !newPalletPick.operator || !newPalletPick.item_id || !newPalletPick.quantity}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Log Pallet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trailer Inspection Modal */}
      {showInspectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg">
            <div className="border-b p-4">
              <h2 className="text-xl font-bold">Trailer Inspection</h2>
              <p className="text-sm text-gray-600 mt-1">Complete inspection and record results</p>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Inspection Result */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Inspection Result <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setInspectionData({ ...inspectionData, passed: true })}
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                      inspectionData.passed === true
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">Pass</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setInspectionData({ ...inspectionData, passed: false })}
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                      inspectionData.passed === false
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-semibold">Fail</span>
                  </button>
                </div>
              </div>
              
              {/* Inspection Notes */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Inspection Notes {inspectionData.passed === false && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  value={inspectionData.notes}
                  onChange={(e) => setInspectionData({ ...inspectionData, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="4"
                  placeholder={inspectionData.passed === false ? "Describe issues found..." : "Optional inspection notes..."}
                  required={inspectionData.passed === false}
                />
              </div>
            </div>
            
            <div className="border-t p-4 flex gap-3">
              <button
                onClick={() => {
                  setShowInspectionModal(false);
                  setInspectionData({ passed: null, notes: '' });
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleInspectionSubmit}
                disabled={inspectionData.passed === null}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Submit Inspection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
